// ============================================================================
// CSV Column Mapper — auto-detect & transform CSV columns to track fields
// ============================================================================

// ============================================================================
// TARGET FIELDS
// ============================================================================

export interface CsvTargetField {
  key: string;
  label: string;
  required: boolean;
  type: 'number' | 'string' | 'datetime' | 'classification';
}

export const TARGET_FIELDS: CsvTargetField[] = [
  { key: 'latitude',       label: 'Latitude',          required: true,  type: 'number' },
  { key: 'longitude',      label: 'Longitude',         required: true,  type: 'number' },
  { key: 'timestamp',      label: 'Timestamp',         required: false, type: 'datetime' },
  { key: 'altitude',       label: 'Altitude (MSL)',    required: false, type: 'number' },
  { key: 'altitudeAGL',    label: 'Altitude (AGL)',    required: false, type: 'number' },
  { key: 'speed',          label: 'Ground Speed',      required: false, type: 'number' },
  { key: 'verticalSpeed',  label: 'Vertical Speed',    required: false, type: 'number' },
  { key: 'heading',        label: 'Heading',           required: false, type: 'number' },
  { key: 'classification', label: 'Classification',    required: false, type: 'classification' },
  { key: 'confidence',     label: 'Confidence',        required: false, type: 'number' },
  { key: 'callsign',       label: 'Callsign',          required: false, type: 'string' },
  { key: 'trackId',        label: 'Track ID',          required: false, type: 'string' },
  { key: 'icaoAddress',    label: 'ICAO Address',      required: false, type: 'string' },
  { key: 'registration',   label: 'Registration',      required: false, type: 'string' },
  { key: 'model',          label: 'Model',             required: false, type: 'string' },
  { key: 'manufacturer',   label: 'Manufacturer',      required: false, type: 'string' },
  { key: 'serialNumber',   label: 'Serial Number',     required: false, type: 'string' },
];

const TARGET_FIELD_MAP = new Map(TARGET_FIELDS.map((f) => [f.key, f]));

// ============================================================================
// COLUMN ALIASES — maps common CSV header names to target field keys
// ============================================================================

export const COLUMN_ALIASES: Record<string, string> = {
  // Latitude
  lat: 'latitude',
  latitude: 'latitude',
  lat_deg: 'latitude',
  lat_dd: 'latitude',
  laty: 'latitude',
  y: 'latitude',

  // Longitude
  lon: 'longitude',
  lng: 'longitude',
  longitude: 'longitude',
  long: 'longitude',
  lon_deg: 'longitude',
  lon_dd: 'longitude',
  x: 'longitude',

  // Timestamp
  timestamp: 'timestamp',
  time: 'timestamp',
  datetime: 'timestamp',
  date_time: 'timestamp',
  utc: 'timestamp',
  utc_time: 'timestamp',
  detected_at: 'timestamp',
  observed_at: 'timestamp',
  created_at: 'timestamp',
  date: 'timestamp',
  epoch: 'timestamp',
  unix_time: 'timestamp',

  // Altitude MSL
  altitude: 'altitude',
  alt: 'altitude',
  altitude_msl: 'altitude',
  alt_msl: 'altitude',
  altitudemsl: 'altitude',
  elevation: 'altitude',
  height_msl: 'altitude',
  geoaltitude: 'altitude',
  geo_altitude: 'altitude',
  height: 'altitude',
  baro_altitude: 'altitude',
  baroaltitude: 'altitude',

  // Altitude AGL
  altitude_agl: 'altitudeAGL',
  alt_agl: 'altitudeAGL',
  height_agl: 'altitudeAGL',
  agl: 'altitudeAGL',

  // Ground Speed
  speed: 'speed',
  groundspeed: 'speed',
  ground_speed: 'speed',
  velocity: 'speed',
  spd: 'speed',
  gs: 'speed',
  horizontal_speed: 'speed',

  // Vertical Speed
  vertical_speed: 'verticalSpeed',
  verticalspeed: 'verticalSpeed',
  vspeed: 'verticalSpeed',
  climb_rate: 'verticalSpeed',
  vs: 'verticalSpeed',
  vertical_rate: 'verticalSpeed',
  vert_rate: 'verticalSpeed',

  // Heading
  heading: 'heading',
  course: 'heading',
  bearing: 'heading',
  hdg: 'heading',
  track: 'heading',
  direction: 'heading',
  true_track: 'heading',

  // Classification
  classification: 'classification',
  class: 'classification',
  type: 'classification',
  category: 'classification',
  object_type: 'classification',
  aircraft_type: 'classification',

  // Confidence
  confidence: 'confidence',
  conf: 'confidence',
  probability: 'confidence',
  score: 'confidence',

  // Track ID
  trackid: 'trackId',
  track_id: 'trackId',
  id: 'trackId',
  detection_id: 'trackId',
  object_id: 'trackId',

  // Callsign
  callsign: 'callsign',
  call_sign: 'callsign',
  flight: 'callsign',
  flight_number: 'callsign',

  // ICAO Address
  icao: 'icaoAddress',
  icao_address: 'icaoAddress',
  icao24: 'icaoAddress',
  icaoaddress: 'icaoAddress',
  hex: 'icaoAddress',

  // Registration
  registration: 'registration',
  tail: 'registration',
  tail_number: 'registration',
  reg: 'registration',

  // Model
  model: 'model',
  aircraft_model: 'model',

  // Manufacturer
  manufacturer: 'manufacturer',
  make: 'manufacturer',

  // Serial Number
  serial_number: 'serialNumber',
  serialnumber: 'serialNumber',
  serial: 'serialNumber',
  sn: 'serialNumber',
};

// ============================================================================
// AUTO-DETECT MAPPING
// ============================================================================

/**
 * Auto-detect CSV column → target field mappings from header names.
 * Returns { csvColumnName: targetFieldKey } for matched columns.
 */
export function autoDetectMapping(headers: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  const usedTargets = new Set<string>();

  for (const header of headers) {
    const normalized = header.toLowerCase().trim().replace(/\s+/g, '_');
    const target = COLUMN_ALIASES[normalized];

    if (target && !usedTargets.has(target)) {
      mapping[header] = target;
      usedTargets.add(target);
    }
  }

  return mapping;
}

// ============================================================================
// APPLY MAPPING
// ============================================================================

/**
 * Transform raw CSV rows using the user-confirmed column mapping.
 * Returns mapped JSON objects ready for ingestion + count of skipped rows.
 */
export function applyMapping(
  rows: Record<string, string>[],
  mapping: Record<string, string>,
): { mapped: Record<string, unknown>[]; skipped: number } {
  const mapped: Record<string, unknown>[] = [];
  let skipped = 0;

  for (const row of rows) {
    const obj: Record<string, unknown> = {};
    let hasLat = false;
    let hasLon = false;

    for (const [csvCol, targetKey] of Object.entries(mapping)) {
      if (!targetKey) continue; // skipped column

      const rawValue = row[csvCol];
      if (rawValue === undefined || rawValue === null || rawValue === '') continue;

      const field = TARGET_FIELD_MAP.get(targetKey);
      if (!field) continue;

      switch (field.type) {
        case 'number': {
          const num = parseFloat(rawValue);
          if (!isNaN(num)) {
            obj[targetKey] = num;
            if (targetKey === 'latitude') hasLat = true;
            if (targetKey === 'longitude') hasLon = true;
          }
          break;
        }
        case 'datetime': {
          // Try parsing as ISO string, epoch seconds, or epoch milliseconds
          let parsed: Date | null = null;
          const numVal = Number(rawValue);
          if (!isNaN(numVal)) {
            // Epoch: if < 1e12 assume seconds, otherwise milliseconds
            parsed = new Date(numVal < 1e12 ? numVal * 1000 : numVal);
          } else {
            parsed = new Date(rawValue);
          }
          if (parsed && !isNaN(parsed.getTime())) {
            obj[targetKey] = parsed.toISOString();
          }
          break;
        }
        case 'string':
        case 'classification':
        default:
          obj[targetKey] = rawValue.trim();
          break;
      }
    }

    // Skip rows missing required lat/lon
    if (!hasLat || !hasLon) {
      skipped++;
      continue;
    }

    // Validate lat/lon ranges
    const lat = obj.latitude as number;
    const lon = obj.longitude as number;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      skipped++;
      continue;
    }

    mapped.push(obj);
  }

  return { mapped, skipped };
}

// ============================================================================
// VALIDATE MAPPING
// ============================================================================

/**
 * Check that required fields (latitude, longitude) are mapped.
 */
export function validateMapping(
  mapping: Record<string, string>,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const mappedTargets = new Set(Object.values(mapping).filter(Boolean));

  for (const field of TARGET_FIELDS) {
    if (field.required && !mappedTargets.has(field.key)) {
      errors.push(`Required field "${field.label}" is not mapped to any CSV column`);
    }
  }

  return { valid: errors.length === 0, errors };
}
